/**
 * LabelDeployConfirmDialog
 * Confirmation dialog shown before deploying labels to a tenant.
 * Lists all selected labels and their current status for user review.
 */

import React from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Text,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import type { LabelEntity } from '../../services/types';
import { StatusBadge } from '../StatusBadge';
import type { LabelUIStatus } from '../../slices/labelDeploymentSlice';
import { DIALOG_STYLES, CONTAINER_STYLES, SECTION_HEADER_STYLES, COLORS, TYPOGRAPHY } from '../policies/constants';

function getLabelStatus(uiStatus: LabelUIStatus) {
  if (uiStatus === 'Deployed') return 'Available' as const;
  if (uiStatus === 'InProgress') return 'In Progress' as const;
  if (uiStatus === 'DeploymentFailed') return 'Failed' as const;
  return 'Not Available' as const;
}

type Props = {
  open: boolean;
  labels: LabelEntity[];
  tenantId: string;
  tenantDisplay: string;
  baseTenantName?: string;
  /** Configured prefix for the target tenant (from TenantConfig). '' / undefined = none. */
  prefix?: string;
  deploying?: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

export default function LabelDeployConfirmDialog({
  open,
  labels,
  tenantId,
  tenantDisplay,
  baseTenantName,
  prefix,
  deploying = false,
  onConfirm,
  onClose,
}: Props) {
  const base = baseTenantName || 'Base Tenant';
  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface style={DIALOG_STYLES.surface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                aria-label="Close"
                onClick={onClose}
              />
            }
          >
            Deploy Labels – {base} → {tenantDisplay}
          </DialogTitle>

          <DialogContent style={{ padding: 0 }}>
            <div style={CONTAINER_STYLES.infoMessage}>
              <Text style={{ ...TYPOGRAPHY.body, color: COLORS.gray600 } as React.CSSProperties}>
                The following{' '}
                <strong>
                  {labels.length} label{labels.length !== 1 ? 's' : ''}
                </strong>{' '}
                will be deployed to <strong>{tenantDisplay}</strong>:
              </Text>
              {prefix && (
                <Text
                  style={
                    {
                      ...TYPOGRAPHY.body,
                      color: COLORS.gray600,
                      display: 'block',
                      marginTop: 8,
                    } as React.CSSProperties
                  }
                >
                  Parent and standalone labels will be prefixed with{' '}
                  <strong>{prefix}</strong> when first created; existing labels are never renamed;
                  child labels are not prefixed.
                </Text>
              )}
            </div>

            <div
              style={{
                border: '1px solid #e5e5e5',
                borderRadius: 6,
                overflow: 'hidden',
                maxHeight: 320,
                overflowY: 'auto',
                margin: '16px 24px',
              }}
            >
              {/* Header row */}
              <div
                style={{
                  ...SECTION_HEADER_STYLES.sticky,
                  display: 'grid',
                  gridTemplateColumns: 'minmax(160px,1.5fr) minmax(120px,1fr) auto',
                }}
              >
                <div>Label</div>
                <div>Parent</div>
                <div>Current status</div>
              </div>

              {/* Label rows */}
              {labels.map((label, i) => {
                const td = label.tenantDeployments.find(
                  t => t.tenantId.toLowerCase() === tenantId.toLowerCase(),
                );
                const uiStatus: LabelUIStatus =
                  td?.deploymentStatus === 'Deployed' ? 'Deployed' : 'NotDeployed';
                return (
                  <div
                    key={label.entityId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(160px,1.5fr) minmax(120px,1fr) auto',
                      padding: '10px 16px',
                      borderBottom: i === labels.length - 1 ? 'none' : '1px solid #f0f0f0',
                      alignItems: 'center',
                      fontSize: 14,
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{label.data.displayName}</div>
                    <div style={{ color: COLORS.gray600, fontSize: 13 }}>
                      {label.data.parentLabelDisplayName ?? <em>—</em>}
                    </div>
                    <div>
                      <StatusBadge status={getLabelStatus(uiStatus)} />
                    </div>
                  </div>
                );
              })}
            </div>
          </DialogContent>

          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={deploying}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={onConfirm} disabled={deploying}>
              {deploying ? 'Deploying…' : 'Deploy'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
